var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from "react";
import { ChevronLeftIcon, ChevronRightIcon, ListBulletIcon } from "@radix-ui/react-icons";
import { Dialog } from "radix-ui";
import "./categoryNavigation.css";
import { RadixButton } from "../primitives/RadixButton";
import { DealsSelection } from "../dealsSelection";
import { SpecialsFlyout } from "../specialsFlyout";
import { RadixDialogue } from "../primitives/RadixDialogue";
import GoogleAnalytics from "../../analytics/google";
import { useCart, useMarketing } from "../../providers";
import { isAvailableForOrderMode } from "../utility/cateringMenuAvailability";
var toBoolean = function (value) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value === 1;
    }
    if (typeof value === "string") {
        var normalizedValue = value.trim().toLowerCase();
        return normalizedValue === "1" || normalizedValue === "true" || normalizedValue === "yes";
    }
    return false;
};
var getRelativeUri = function () {
    var categoryWindow = window;
    return typeof categoryWindow.relativeURI === "function" ? categoryWindow.relativeURI() : "";
};
var trimPath = function (path) { return path.split("/").filter(Boolean).join("/"); };
var getStoreBaseUri = function (useCateringPath) {
    var _a;
    var categoryWindow = window;
    var store = (_a = categoryWindow.session) === null || _a === void 0 ? void 0 : _a.store;
    var storePath = trimPath((store === null || store === void 0 ? void 0 : store.store_url_path) || (store === null || store === void 0 ? void 0 : store.url_path) || (store === null || store === void 0 ? void 0 : store.urlslug) || "");
    var targetPath = useCateringPath ? trimPath("".concat(storePath, "/catering")) : storePath;
    if ((store === null || store === void 0 ? void 0 : store.fqdn) && targetPath) {
        return "".concat(document.location.protocol, "//").concat(store.fqdn, "/").concat(targetPath);
    }
    var relativeUri = getRelativeUri();
    if (useCateringPath) {
        return relativeUri.endsWith("/catering") ? relativeUri : "".concat(relativeUri, "/catering");
    }
    if (relativeUri.endsWith("/catering")) {
        return relativeUri.slice(0, -"/catering".length);
    }
    return relativeUri;
};
var CATEGORY_HASH_PREFIX = "#category-";
var SPECIALS_DEALS_CATEGORY_ID = "specials-deals";
var PROMO_DEALS_CATEGORY_ID = "deals";
var CATERING_MENU_CATEGORY_ID = "catering-menu";
var getSessionServiceTypes = function (value) {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(function (entry) {
        return Boolean(entry && typeof entry === "object" && "id" in entry);
    });
};
var getSessionItemNavigations = function (value) {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(function (entry) {
        return Boolean(entry && typeof entry === "object" && "id" in entry);
    });
};
var getAllowedAvailabilityValues = function (targetIsCatering) {
    return targetIsCatering ? ["catering", "both"] : ["not_catering", "both"];
};
var getConflictingCartItems = function (items, itemNavigations, targetIsCatering) {
    var allowedAvailabilityValues = getAllowedAvailabilityValues(targetIsCatering);
    var itemNavigationAvailabilityById = new Map();
    itemNavigations.forEach(function (itemNavigation) {
        if (itemNavigation.id === undefined || itemNavigation.id === null) {
            return;
        }
        itemNavigationAvailabilityById.set(String(itemNavigation.id), itemNavigation.catering_menu_availability);
    });
    return items.filter(function (item) {
        if (item.is_addon || item.addon_id || !item.item_navigation_id) {
            return false;
        }
        var itemNavigationAvailability = itemNavigationAvailabilityById.get(String(item.item_navigation_id)) || "not_catering";
        return !allowedAvailabilityValues.includes(itemNavigationAvailability);
    });
};
var parseCategoryIdFromHash = function (hash) {
    if (!hash.startsWith(CATEGORY_HASH_PREFIX)) {
        return null;
    }
    var rawCategoryId = hash.slice(CATEGORY_HASH_PREFIX.length);
    if (!rawCategoryId) {
        return null;
    }
    try {
        return decodeURIComponent(rawCategoryId);
    }
    catch (_a) {
        return rawCategoryId;
    }
};
var isMenuPagePath = function (pathname) {
    var normalized = pathname.toLowerCase();
    if (!normalized.startsWith("/menu"))
        return false;
    var remainder = normalized.slice("/menu".length);
    return remainder === "" || remainder === "/";
};
var isCateringMenuPath = function (pathname) {
    var pathSegments = pathname.toLowerCase().split("/").filter(Boolean);
    var cateringSegmentIndex = pathSegments.indexOf("catering");
    if (cateringSegmentIndex < 0) {
        return false;
    }
    return pathSegments[cateringSegmentIndex + 1] === "menu";
};
export var CategoryNavigation = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var categories = _a.categories, className = _a.className;
    var categoryWindow = window;
    var items = useCart().items;
    var specials = useMarketing().specials;
    var isCateringMenu = isCateringMenuPath(window.location.pathname) || toBoolean((_c = (_b = categoryWindow.session) === null || _b === void 0 ? void 0 : _b.store) === null || _c === void 0 ? void 0 : _c.is_catering);
    var sessionDeals = Array.isArray((_d = categoryWindow.session) === null || _d === void 0 ? void 0 : _d.deals) ? categoryWindow.session.deals : [];
    var visibleSessionDeals = sessionDeals.filter(function (deal) {
        if (!deal || typeof deal !== "object") {
            return false;
        }
        return isAvailableForOrderMode(deal.catering_menu_availability, isCateringMenu);
    });
    var hasSpecialsDeals = specials.some(function (special) {
        return isAvailableForOrderMode(special.catering_menu_availability, isCateringMenu);
    });
    var hasPromoDeals = visibleSessionDeals.length > 0;
    var cateringServiceTypes = React.useMemo(function () {
        var _a;
        return getSessionServiceTypes((_a = categoryWindow.session) === null || _a === void 0 ? void 0 : _a.service_types).filter(function (serviceType) {
            return toBoolean(serviceType.catering_enabled);
        });
    }, [(_e = categoryWindow.session) === null || _e === void 0 ? void 0 : _e.service_types]);
    var cateringIsNotEnabled = isCateringMenu && cateringServiceTypes.length === 0;
    var regularMenuHref = React.useMemo(function () { return getStoreBaseUri(false); }, []);
    var _h = React.useState(cateringIsNotEnabled), cateringNotEnabledModalIsOpen = _h[0], setCateringNotEnabledModalIsOpen = _h[1];
    var _j = React.useState(false), isSpecialsFlyOutOpen = _j[0], setIsSpecialsFlyOutOpen = _j[1];
    var _k = React.useState(false), isDealsFlyOutOpen = _k[0], setIsDealsFlyOutOpen = _k[1];
    var _l = React.useState(false), isMobileCategorySheetOpen = _l[0], setIsMobileCategorySheetOpen = _l[1];
    var _m = React.useState(null), pendingOrderModeSwitch = _m[0], setPendingOrderModeSwitch = _m[1];
    var navRef = React.useRef(null);
    var scrollRef = React.useRef(null);
    var buttonRefs = React.useRef({});
    var _o = React.useState(false), canScrollLeft = _o[0], setCanScrollLeft = _o[1];
    var _p = React.useState(false), canScrollRight = _p[0], setCanScrollRight = _p[1];
    var _q = React.useState(function () { return parseCategoryIdFromHash(window.location.hash); }), activeCategoryId = _q[0], setActiveCategoryId = _q[1];
    var getVisibleStickyOrderIntentHeight = React.useCallback(function () {
        var orderIntentNavbar = document.querySelector(".order-intent-navbar");
        if (!(orderIntentNavbar instanceof HTMLElement)) {
            return 0;
        }
        if (orderIntentNavbar.getAttribute("aria-hidden") === "true") {
            return 0;
        }
        var computedStyle = window.getComputedStyle(orderIntentNavbar);
        var isStickyLike = computedStyle.position === "sticky" || computedStyle.position === "fixed";
        if (!isStickyLike || computedStyle.display === "none" || computedStyle.visibility === "hidden") {
            return 0;
        }
        var rect = orderIntentNavbar.getBoundingClientRect();
        var intersectsViewport = rect.bottom > 0 && rect.top < window.innerHeight;
        if (!intersectsViewport) {
            return 0;
        }
        return rect.height;
    }, []);
    var getCategorySectionElement = React.useCallback(function (categoryId) {
        var _a, _b, _c;
        return (_c = (_b = (_a = document.getElementById("category-".concat(categoryId))) !== null && _a !== void 0 ? _a : document.querySelector(".category-navigation-section[data-category-id=\"".concat(categoryId, "\"]"))) !== null && _b !== void 0 ? _b : document.getElementById(categoryId)) !== null && _c !== void 0 ? _c : document.querySelector("[data-category-id=\"".concat(categoryId, "\"]"));
    }, []);
    var navigableCategories = React.useMemo(function () {
        var _a, _b, _c, _d, _e;
        var extraCategories = [];
        if (hasPromoDeals) {
            extraCategories.push({
                id: PROMO_DEALS_CATEGORY_ID,
                name: "Promos",
            });
        }
        if (hasSpecialsDeals) {
            extraCategories.push({
                id: SPECIALS_DEALS_CATEGORY_ID,
                name: (_c = (_b = (_a = categoryWindow.session) === null || _a === void 0 ? void 0 : _a.store_branding) === null || _b === void 0 ? void 0 : _b.special_name_plural) !== null && _c !== void 0 ? _c : 'Deals',
            });
        }
        if (toBoolean((_e = (_d = categoryWindow.session) === null || _d === void 0 ? void 0 : _d.store) === null || _e === void 0 ? void 0 : _e.catering_site_enabled)) {
            extraCategories.push({
                id: CATERING_MENU_CATEGORY_ID,
                name: isCateringMenu ? "Regular Menu" : "Catering",
            });
        }
        return __spreadArray(__spreadArray([], extraCategories, true), categories, true);
    }, [categories, (_g = (_f = categoryWindow.session) === null || _f === void 0 ? void 0 : _f.store_branding) === null || _g === void 0 ? void 0 : _g.special_name_plural, hasPromoDeals, hasSpecialsDeals, isCateringMenu]);
    var getCategorySections = React.useCallback(function () {
        return navigableCategories
            .map(function (category) {
            var categoryId = String(category.id);
            var element = getCategorySectionElement(categoryId);
            if (!(element instanceof HTMLElement)) {
                return null;
            }
            return { categoryId: categoryId, element: element };
        })
            .filter(function (section) { return section !== null; });
    }, [getCategorySectionElement, navigableCategories]);
    var getStickyOffset = React.useCallback(function () {
        var _a, _b;
        var header = document.querySelector(".radix-navbar--sticky");
        var headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
        var orderIntentHeight = getVisibleStickyOrderIntentHeight();
        var navHeight = (_b = (_a = navRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) !== null && _b !== void 0 ? _b : 0;
        var stickyOffset = headerHeight + orderIntentHeight + navHeight;
        return stickyOffset;
    }, [getVisibleStickyOrderIntentHeight]);
    var updateScrollState = React.useCallback(function () {
        var container = scrollRef.current;
        if (!container) {
            return;
        }
        var maxScrollLeft = container.scrollWidth - container.clientWidth;
        var threshold = 2;
        setCanScrollLeft(container.scrollLeft > threshold);
        setCanScrollRight(container.scrollLeft < maxScrollLeft - threshold);
    }, []);
    var scrollActiveButtonIntoView = React.useCallback(function (categoryId) {
        var container = scrollRef.current;
        var button = buttonRefs.current[categoryId];
        if (!container || !button || window.innerWidth > 768) {
            return;
        }
        var containerRect = container.getBoundingClientRect();
        var buttonRect = button.getBoundingClientRect();
        var horizontalPadding = 40;
        var isHiddenOnLeft = buttonRect.left < containerRect.left + horizontalPadding;
        var isHiddenOnRight = buttonRect.right > containerRect.right - horizontalPadding;
        if (!isHiddenOnLeft && !isHiddenOnRight) {
            return;
        }
        var targetScrollLeft = button.offsetLeft - ((container.clientWidth - button.clientWidth) / 2);
        var maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);
        container.scrollTo({
            left: Math.min(Math.max(targetScrollLeft, 0), maxScrollLeft),
            behavior: "smooth",
        });
    }, []);
    React.useEffect(function () {
        updateScrollState();
        var container = scrollRef.current;
        if (!container) {
            return;
        }
        container.addEventListener("scroll", updateScrollState, { passive: true });
        window.addEventListener("resize", updateScrollState);
        return function () {
            container.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [navigableCategories, updateScrollState]);
    var scrollByAmount = function (direction) {
        var container = scrollRef.current;
        if (!container) {
            return;
        }
        var amount = Math.max(container.clientWidth * 0.7, 160);
        container.scrollBy({
            left: direction === "left" ? -amount : amount,
            behavior: "smooth",
        });
    };
    var scrollToCategory = React.useCallback(function (categoryId, behavior) {
        if (behavior === void 0) { behavior = "smooth"; }
        var categorySection = getCategorySectionElement(categoryId);
        if (!(categorySection instanceof HTMLElement)) {
            return false;
        }
        categorySection.scrollIntoView({
            block: "start",
            behavior: behavior,
        });
        setActiveCategoryId(categoryId);
        scrollActiveButtonIntoView(categoryId);
        return true;
    }, [getCategorySectionElement, scrollActiveButtonIntoView]);
    var scrollToHashCategory = React.useCallback(function () {
        var categoryId = parseCategoryIdFromHash(window.location.hash);
        if (!categoryId) {
            return;
        }
        setActiveCategoryId(categoryId);
        var timeoutId;
        var startedAt = Date.now();
        var maxDurationMs = 3000;
        var attemptScroll = function () {
            var elapsed = Date.now() - startedAt;
            var behavior = elapsed < 100 ? "auto" : "smooth";
            var didScroll = scrollToCategory(categoryId, behavior);
            if (didScroll || elapsed >= maxDurationMs) {
                return;
            }
            timeoutId = window.setTimeout(attemptScroll, 100);
        };
        requestAnimationFrame(attemptScroll);
        return function () {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [scrollToCategory]);
    React.useEffect(function () {
        return scrollToHashCategory();
    }, [navigableCategories, scrollToHashCategory]);
    React.useEffect(function () {
        var onHashChange = function () {
            var categoryId = parseCategoryIdFromHash(window.location.hash);
            setActiveCategoryId(categoryId);
            scrollToHashCategory();
        };
        window.addEventListener("hashchange", onHashChange);
        return function () {
            window.removeEventListener("hashchange", onHashChange);
        };
    }, [scrollToHashCategory]);
    React.useEffect(function () {
        var root = document.documentElement;
        var updateCategoryScrollOffset = function () {
            var _a, _b;
            var header = document.querySelector(".radix-navbar--sticky");
            var headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
            var orderIntentNavbar = document.querySelector(".order-intent-navbar");
            var orderIntentHeight = getVisibleStickyOrderIntentHeight();
            var navHeight = (_b = (_a = navRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) !== null && _b !== void 0 ? _b : 0;
            var stickyOffset = headerHeight + orderIntentHeight + navHeight;
            var marginTop = Math.max(Math.round(stickyOffset), 0);
            root.style.setProperty("--category-navigation-scroll-offset", "".concat(marginTop, "px"));
        };
        updateCategoryScrollOffset();
        window.addEventListener("resize", updateCategoryScrollOffset);
        var observers = [];
        if (typeof ResizeObserver === "function") {
            [document.querySelector(".radix-navbar--sticky"), document.querySelector(".order-intent-navbar"), navRef.current]
                .forEach(function (element) {
                if (element instanceof HTMLElement) {
                    var observer = new ResizeObserver(updateCategoryScrollOffset);
                    observer.observe(element);
                    observers.push(observer);
                }
            });
        }
        return function () {
            window.removeEventListener("resize", updateCategoryScrollOffset);
            observers.forEach(function (observer) { return observer.disconnect(); });
            root.style.removeProperty("--category-navigation-scroll-offset");
        };
    }, [getStickyOffset, getVisibleStickyOrderIntentHeight]);
    React.useEffect(function () {
        var updateActiveCategoryFromScroll = function () {
            var _a;
            var categorySections = getCategorySections();
            if (categorySections.length === 0) {
                return;
            }
            var stickyOffset = getStickyOffset();
            var activationLine = stickyOffset + 24;
            var nextActiveCategoryId = categorySections[categorySections.length - 1].categoryId;
            for (var index = 0; index < categorySections.length; index += 1) {
                var section = categorySections[index];
                var sectionTop = section.element.getBoundingClientRect().top;
                var nextSection = categorySections[index + 1];
                var nextSectionTop = (_a = nextSection === null || nextSection === void 0 ? void 0 : nextSection.element.getBoundingClientRect().top) !== null && _a !== void 0 ? _a : Number.POSITIVE_INFINITY;
                if (sectionTop <= activationLine && nextSectionTop > activationLine) {
                    nextActiveCategoryId = section.categoryId;
                    break;
                }
                if (sectionTop > activationLine) {
                    nextActiveCategoryId = section.categoryId;
                    break;
                }
            }
            setActiveCategoryId(function (currentCategoryId) { return currentCategoryId === nextActiveCategoryId
                ? currentCategoryId
                : nextActiveCategoryId; });
        };
        updateActiveCategoryFromScroll();
        var animationFrameId = 0;
        var onScroll = function () {
            if (animationFrameId !== 0) {
                return;
            }
            animationFrameId = window.requestAnimationFrame(function () {
                animationFrameId = 0;
                updateActiveCategoryFromScroll();
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", updateActiveCategoryFromScroll);
        return function () {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", updateActiveCategoryFromScroll);
            if (animationFrameId !== 0) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [getCategorySections, getStickyOffset]);
    React.useEffect(function () {
        if (!activeCategoryId) {
            return;
        }
        scrollActiveButtonIntoView(activeCategoryId);
    }, [activeCategoryId, scrollActiveButtonIntoView]);
    React.useEffect(function () {
        var updateToastOffset = function () {
            if (!(navRef.current instanceof HTMLElement)) {
                return;
            }
            var navBottom = navRef.current.getBoundingClientRect().bottom;
            var toastTopOffset = Math.max(Math.round(navBottom + 8), 0);
            document.documentElement.style.setProperty("--ui-toast-top-offset", "".concat(toastTopOffset, "px"));
        };
        updateToastOffset();
        var animationFrameId = 0;
        var onScroll = function () {
            if (animationFrameId !== 0) {
                return;
            }
            animationFrameId = window.requestAnimationFrame(function () {
                animationFrameId = 0;
                updateToastOffset();
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", updateToastOffset);
        return function () {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", updateToastOffset);
            if (animationFrameId !== 0) {
                window.cancelAnimationFrame(animationFrameId);
            }
            document.documentElement.style.removeProperty("--ui-toast-top-offset");
        };
    }, []);
    var confirmOrderModeSwitch = React.useCallback(function () {
        if (!pendingOrderModeSwitch) {
            return;
        }
        window.location.assign(pendingOrderModeSwitch.href);
    }, [pendingOrderModeSwitch]);
    var cancelOrderModeSwitch = React.useCallback(function () {
        setPendingOrderModeSwitch(null);
    }, []);
    var navigateToCategory = function (category) {
        var _a;
        var categoryId = String(category.id);
        if (categoryId === SPECIALS_DEALS_CATEGORY_ID) {
            setActiveCategoryId(categoryId);
            setIsSpecialsFlyOutOpen(true);
            return;
        }
        if (categoryId === PROMO_DEALS_CATEGORY_ID) {
            setActiveCategoryId(categoryId);
            setIsDealsFlyOutOpen(true);
            return;
        }
        if (categoryId === CATERING_MENU_CATEGORY_ID) {
            var targetIsCatering = !isCateringMenu;
            var menuHref_1 = targetIsCatering
                ? "".concat(getStoreBaseUri(true), "/menu")
                : "".concat(getStoreBaseUri(false), "/menu");
            var conflictingItems = getConflictingCartItems(items, getSessionItemNavigations((_a = categoryWindow.session) === null || _a === void 0 ? void 0 : _a.item_navigations), targetIsCatering);
            if (conflictingItems.length > 0) {
                setIsMobileCategorySheetOpen(false);
                setPendingOrderModeSwitch({
                    href: menuHref_1,
                    targetIsCatering: targetIsCatering,
                    conflictingItems: conflictingItems,
                });
                return;
            }
            setActiveCategoryId(categoryId);
            window.location.assign(menuHref_1);
            return;
        }
        var categoryAnchor = "".concat(CATEGORY_HASH_PREFIX).concat(encodeURIComponent(categoryId));
        var menuHref = "".concat(getRelativeUri(), "/menu").concat(categoryAnchor);
        var isMenuPage = isMenuPagePath(window.location.pathname);
        GoogleAnalytics.viewCategory(category.name);
        setActiveCategoryId(categoryId);
        if (!isMenuPage) {
            document.location.href = menuHref;
            return;
        }
        var didScroll = scrollToCategory(categoryId);
        if (!didScroll) {
            document.location.href = menuHref;
            return;
        }
        history.replaceState(null, "", categoryAnchor);
    };
    var navigateToCategoryFromSheet = function (category) {
        navigateToCategory(category);
        setIsMobileCategorySheetOpen(false);
    };
    return (React.createElement("nav", { ref: navRef, className: ["category-navigation", className].filter(Boolean).join(" "), "aria-label": "Menu categories" },
        React.createElement("div", { className: "category-navigation-inner" },
            React.createElement(RadixButton, { type: "button", className: "category-navigation-mobile-trigger", onClick: function () { return setIsMobileCategorySheetOpen(true); }, "aria-label": "Open all categories" },
                React.createElement(ListBulletIcon, { "aria-hidden": "true" })),
            canScrollLeft ? (React.createElement(RadixButton, { type: "button", className: "category-navigation-scroll category-navigation-scroll--left", onClick: function () { return scrollByAmount("left"); }, "aria-label": "Scroll categories left" },
                React.createElement(ChevronLeftIcon, { "aria-hidden": "true" }))) : null,
            React.createElement("div", { className: "category-navigation-scroll-region", ref: scrollRef },
                React.createElement("ul", { className: "category-navigation-list", role: "list" }, navigableCategories.map(function (category) {
                    var categoryId = String(category.id);
                    var isActive = activeCategoryId === categoryId;
                    return (React.createElement("li", { key: categoryId, className: "category-navigation-list-item" },
                        React.createElement(RadixButton, { type: "button", className: [
                                "category-navigation-link",
                                isActive ? "category-navigation-link--active" : "",
                            ].filter(Boolean).join(" "), ref: function (element) {
                                buttonRefs.current[categoryId] = element;
                            }, onClick: function () { return navigateToCategory(category); }, "aria-current": isActive ? "page" : undefined }, category.name)));
                }))),
            canScrollRight ? (React.createElement(RadixButton, { type: "button", className: "category-navigation-scroll category-navigation-scroll--right", onClick: function () { return scrollByAmount("right"); }, "aria-label": "Scroll categories right" },
                React.createElement(ChevronRightIcon, { "aria-hidden": "true" }))) : null),
        React.createElement(RadixDialogue, { open: cateringNotEnabledModalIsOpen, onOpenChange: setCateringNotEnabledModalIsOpen, overlayClassName: "category-navigation-mode-switch-overlay", contentClassName: "category-navigation-mode-switch-content", useGeneratedDescriptionId: true },
            React.createElement("div", { className: "category-navigation-mode-switch-dialog" },
                React.createElement(Dialog.Title, { className: "category-navigation-mode-switch-title" }, "Catering is currently unavailable"),
                React.createElement(Dialog.Description, { className: "category-navigation-mode-switch-description" }, "Catering ordering is not enabled for this location right now. Please choose the regular menu or contact the store for help with a catering order."),
                React.createElement("div", { className: "category-navigation-mode-switch-actions" },
                    React.createElement(RadixButton, { type: "button", className: "category-navigation-mode-switch-primary", onClick: function () { return window.location.assign(regularMenuHref); } }, "View Regular Menu")))),
        React.createElement(RadixDialogue, { open: isMobileCategorySheetOpen, onOpenChange: setIsMobileCategorySheetOpen, overlayClassName: "category-navigation-mobile-sheet-overlay", contentClassName: "category-navigation-mobile-sheet-content" },
            React.createElement("div", { className: "category-navigation-mobile-sheet" },
                React.createElement("header", { className: "category-navigation-mobile-sheet-header" },
                    React.createElement(Dialog.Title, { className: "category-navigation-mobile-sheet-title" }, "Browse categories"),
                    React.createElement(Dialog.Close, { className: "category-navigation-mobile-sheet-close", "aria-label": "Close category list" },
                        React.createElement("span", { "aria-hidden": "true" }, "\u00D7"))),
                React.createElement("ul", { className: "category-navigation-mobile-sheet-list", role: "list" }, navigableCategories.map(function (category) {
                    var categoryId = String(category.id);
                    var isActive = activeCategoryId === categoryId;
                    return (React.createElement("li", { key: "mobile-".concat(categoryId), className: "category-navigation-mobile-sheet-list-item" },
                        React.createElement(RadixButton, { type: "button", className: [
                                "category-navigation-mobile-sheet-link",
                                isActive ? "category-navigation-mobile-sheet-link--active" : "",
                            ].filter(Boolean).join(" "), onClick: function () { return navigateToCategoryFromSheet(category); }, "aria-current": isActive ? "page" : undefined }, category.name)));
                })))),
        React.createElement(RadixDialogue, { open: pendingOrderModeSwitch !== null, onOpenChange: function (open) {
                if (!open) {
                    setPendingOrderModeSwitch(null);
                }
            }, overlayClassName: "category-navigation-mode-switch-overlay", contentClassName: "category-navigation-mode-switch-content", useGeneratedDescriptionId: true },
            React.createElement("div", { className: "category-navigation-mode-switch-dialog" },
                React.createElement(Dialog.Title, { className: "category-navigation-mode-switch-title" }, "Remove unavailable cart items?"),
                React.createElement(Dialog.Description, { className: "category-navigation-mode-switch-description" },
                    (pendingOrderModeSwitch === null || pendingOrderModeSwitch === void 0 ? void 0 : pendingOrderModeSwitch.targetIsCatering)
                        ? "Some items in your cart are not available for catering orders."
                        : "Some items in your cart are only available for catering orders.",
                    " To switch menus, those items must be removed from your cart."),
                pendingOrderModeSwitch ? (React.createElement("ul", { className: "category-navigation-mode-switch-list", role: "list" },
                    pendingOrderModeSwitch.conflictingItems.slice(0, 5).map(function (item) { return (React.createElement("li", { key: String(item.id) }, item.friendly_name || item.name || "Cart item")); }),
                    pendingOrderModeSwitch.conflictingItems.length > 5 ? (React.createElement("li", null,
                        "and ",
                        pendingOrderModeSwitch.conflictingItems.length - 5,
                        " more item(s)")) : null)) : null,
                React.createElement("div", { className: "category-navigation-mode-switch-actions" },
                    React.createElement(RadixButton, { type: "button", className: "category-navigation-mode-switch-secondary", onClick: cancelOrderModeSwitch }, "Keep my cart"),
                    React.createElement(RadixButton, { type: "button", className: "category-navigation-mode-switch-primary", onClick: confirmOrderModeSwitch }, "Remove items and switch")))),
        React.createElement(SpecialsFlyout, { open: isSpecialsFlyOutOpen, onOpenChange: setIsSpecialsFlyOutOpen }),
        React.createElement(DealsSelection, { deals: visibleSessionDeals, open: isDealsFlyOutOpen, onOpenChange: setIsDealsFlyOutOpen, onSelectionComplete: function () {
                setIsDealsFlyOutOpen(false);
            }, hideHeader: true, presentation: "flyout" })));
};
